import { Link } from "react-router-dom";
import { Check, ArrowRight, MessageCircle, Phone, Truck, Sparkles, Trash2, ArrowUpRight } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { useSEO } from "@/components/site/SEO";
import { SITE, whatsappLink, telLink } from "@/config/site";
import { track } from "@/lib/analytics";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { PackageQuoteForm } from "@/sections/PackageQuoteForm";

const PACKAGES = [
  {
    icon: Truck, name: "Move & Clean Package", featured: false,
    items: ["House or apartment move", "End of tenancy cleaning", "One combined quote", "Flexible scheduling"],
  },
  {
    icon: Sparkles, name: "Move, Clean & Clear Package", featured: true,
    items: ["House or apartment move", "End of tenancy cleaning", "Rubbish and unwanted item removal", "Furniture removal if required", "One combined quote"],
  },
  {
    icon: Trash2, name: "Full Move-Out Package", featured: false,
    items: ["Moving service", "End of tenancy cleaning", "Furniture disassembly / reassembly", "Rubbish removal", "Optional storage move", "Optional oven and carpet cleaning"],
  },
];

const FACTORS = ["Property size", "Moving distance", "Number of items", "Cleaning requirements", "Rubbish volume", "Number of movers required"];

export default function PackagesPage() {
  useLenis();
  useSEO({
    title: "Moving & Cleaning Packages Dublin | GoCleaning Dublin",
    description: "One combined quote for moving and end-of-tenancy cleaning across Dublin and surrounding areas. Move, clean, clear and furniture removal handled by one local team.",
    canonical: `${SITE.url}/moving-cleaning-packages`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service",
      serviceType: "Moving and Cleaning Packages",
      name: "Moving & Cleaning Packages Dublin",
      description: "Combined moving and end-of-tenancy cleaning packages across Dublin and Kildare.",
      areaServed: ["Dublin", "County Kildare"], url: `${SITE.url}/moving-cleaning-packages`,
      provider: { "@type": "CleaningService", name: SITE.name, telephone: SITE.phoneHref, email: SITE.email, url: SITE.url },
    },
  });

  return (
    <div className="App bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-white pt-32 pb-12 sm:pt-36" data-testid="packages-hero">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
                <Truck size={14} /> GoCleaning + GoVans
              </span>
              <h1 className="mt-5 max-w-3xl font-display text-5xl font-black leading-[1.03] tracking-tighter text-slate-900 sm:text-6xl">
                Moving + Cleaning, All in One
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-slate-600">
                One local team for your move, end-of-tenancy cleaning and unwanted item removal across
                Dublin and surrounding areas.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#package-form" data-testid="packages-cta" className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-7 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800">
                  Get One Quote for Everything <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </a>
                <a href={telLink()} onClick={track.phoneClick} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-4 text-base font-bold text-slate-800 hover:border-emerald-600 hover:text-emerald-700"><Phone size={18} /> Call {SITE.phoneDisplay}</a>
                <a href={whatsappLink("Hello, I'd like one combined quote for moving and end-of-tenancy cleaning.")} onClick={() => track.whatsappClick("packages")} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 text-base font-bold text-white hover:scale-105 transition-transform"><MessageCircle size={18} /> WhatsApp</a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Packages */}
        <section className="bg-slate-50 py-16 sm:py-24" data-testid="packages-list">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Choose a combined package</h2>
              <p className="mt-3 max-w-2xl text-slate-600">Pick the mix that suits your move — we confirm one clear quote after we see the details.</p>
            </Reveal>
            <Stagger className="mt-10 grid gap-6 lg:grid-cols-3">
              {PACKAGES.map((p) => (
                <StaggerItem key={p.name}>
                  <div data-testid={`package-${p.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                    className={`relative flex h-full flex-col rounded-3xl border p-7 transition-all ${p.featured ? "border-emerald-700 bg-white shadow-2xl shadow-emerald-700/15 lg:-translate-y-3" : "border-slate-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"}`}>
                    {p.featured && <span className="absolute -top-3 left-7 rounded-full bg-emerald-700 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">Most popular</span>}
                    <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700"><p.icon size={22} /></span>
                    <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                    <ul className="mt-5 flex-1 space-y-3">
                      {p.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-slate-600"><Check size={17} className="mt-0.5 shrink-0 text-emerald-600" /> {it}</li>
                      ))}
                    </ul>
                    <a href="#package-form" data-testid={`package-cta-${p.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-800">
                      Request a combined quote
                    </a>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* Pricing factors */}
        <section className="bg-white py-16" data-testid="packages-factors">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="rounded-3xl border border-slate-200 bg-slate-50/60 p-8 sm:p-10">
                <h2 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">No fixed package prices — here's why</h2>
                <p className="mt-3 text-slate-600">Every move and clean is different, so we give one honest combined quote after we understand the job. Final pricing depends on:</p>
                <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {FACTORS.map((f) => (
                    <li key={f} className="flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-slate-700 shadow-sm"><Check size={16} className="text-emerald-600" /> {f}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Form */}
        <section className="bg-emerald-50 py-16 sm:py-24" data-testid="packages-form-section">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-emerald-700">One combined quote</p>
              <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">Get One Quote for Everything</h2>
              <p className="mt-4 text-lg text-slate-600">Tell us about your move and cleaning and we'll reply with a single, clear quote.</p>
            </div>
            <PackageQuoteForm />
          </div>
        </section>

        {/* GoVans cross-link (brands kept separate) */}
        <section className="bg-white py-16" data-testid="packages-govans">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-start justify-between gap-4 rounded-3xl border border-slate-200 bg-slate-50/60 p-8 sm:flex-row sm:items-center">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900">The moving side is handled by GoVans.ie</h2>
                <p className="mt-1 max-w-2xl text-slate-600">GoCleaning and GoVans are separate businesses run by the same local team. The removals, transport and storage parts of your package are delivered through GoVans.ie.</p>
              </div>
              <a href={SITE.goVans.url} target="_blank" rel="noopener noreferrer" data-testid="packages-visit-govans" className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-slate-900 px-6 py-3.5 font-bold text-white transition-colors hover:bg-slate-800">
                Visit GoVans.ie <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
            <p className="mt-6 text-sm text-slate-500">Looking for cleaning only? <Link to="/services" className="font-bold text-emerald-700 hover:underline">View GoCleaning services</Link>.</p>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
